/**
 * NetworkChannelsPage — Topic Channels: list + channel detail
 * Routes:
 *   /network/channels             → list
 *   /network/channels/:channelId  → channel posts
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Hash, Plus, Bell, BellOff, ArrowLeft,
  Globe, Lock, ShieldCheck, MessageSquare,
  Send, RefreshCw,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  networkChannelApi,
  type NetworkChannel, type ChannelPost,
} from '@/lib/network-api';
import { useBooxSocket } from '@/hooks/useBooxSocket';
import { cn } from '@/lib/utils';

const VISIBILITY_ICON: Record<string, React.ElementType> = {
  public:         Globe,
  verified_only:  ShieldCheck,
  invite:         Lock,
  contract:       Lock,
};

const CHANNEL_TYPE_LABEL: Record<string, string> = {
  trade_lane:  'Trade Lane',
  port:        'Port',
  cargo_type:  'Cargo Type',
  regulation:  'Regulation',
  company:     'Company',
  group:       'Group',
};

// ─── Channel card ─────────────────────────────────────────────────────────────

function ChannelCard({
  channel,
  onClick,
  onToggleSub,
  subLoading,
}: {
  channel: NetworkChannel;
  onClick: () => void;
  onToggleSub: (id: string, subscribed: boolean) => void;
  subLoading: string | null;
}) {
  const Icon = VISIBILITY_ICON[channel.visibility] ?? Globe;

  return (
    <div
      className="group flex cursor-pointer flex-col gap-2 rounded-lg border border-border bg-card p-4 transition-all hover:border-primary/30 hover:shadow-md"
      onClick={onClick}
    >
      <div className="flex items-start gap-3">
        <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <Hash className="size-4" />
        </div>
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <div className="flex items-center gap-2">
            <span className="truncate font-medium text-foreground">{channel.name}</span>
            <Icon className="size-3.5 shrink-0 text-muted-foreground" />
          </div>
          {channel.description && (
            <span className="line-clamp-2 text-xs text-muted-foreground">{channel.description}</span>
          )}
        </div>
        <Button
          size="sm"
          variant={channel.is_subscribed ? 'outline' : 'default'}
          className="shrink-0"
          disabled={subLoading === channel.id}
          onClick={(e) => {
            e.stopPropagation();
            onToggleSub(channel.id, channel.is_subscribed);
          }}
        >
          {channel.is_subscribed ? (
            <><BellOff className="mr-1 size-3.5" />Unsubscribe</>
          ) : (
            <><Bell className="mr-1 size-3.5" />Subscribe</>
          )}
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
        <Badge variant="secondary">{CHANNEL_TYPE_LABEL[channel.channel_type] ?? channel.channel_type}</Badge>
        <span>{channel.member_count} members</span>
        <span>·</span>
        <span>{channel.post_count} posts</span>
        {channel.regions.length > 0 && (
          <span>· {channel.regions.slice(0, 3).join(', ')}</span>
        )}
      </div>
    </div>
  );
}

// ─── Post card ────────────────────────────────────────────────────────────────

function PostCard({ post, channelId }: { post: ChannelPost; channelId: string }) {
  return (
    <div className="flex flex-col gap-2 rounded-lg border border-border bg-card p-4">
      <div className="flex items-center gap-2">
        <div className="flex size-7 items-center justify-center rounded-full bg-muted text-xs font-medium">
          {post.author_name.charAt(0).toUpperCase()}
        </div>
        <span className="text-sm font-medium">{post.author_name}</span>
        <span className="text-xs text-muted-foreground">
          {new Date(post.created_at).toLocaleDateString('en-SG', { month: 'short', day: 'numeric' })}
        </span>
        {post.post_type !== 'discussion' && (
          <Badge variant="secondary" className="text-xs">{post.post_type}</Badge>
        )}
      </div>
      {post.title && <h3 className="font-medium text-sm">{post.title}</h3>}
      <p className="text-sm leading-relaxed text-foreground whitespace-pre-wrap">{post.body}</p>
      <div className="flex items-center gap-3 text-xs text-muted-foreground">
        <button className="flex items-center gap-1 hover:text-foreground">
          <MessageSquare className="size-3.5" />{post.reply_count} replies
        </button>
      </div>
    </div>
  );
}

// ─── Channel detail ───────────────────────────────────────────────────────────

function ChannelDetailView({
  channelId,
  onBack,
}: {
  channelId: string;
  onBack?: () => void;
}) {
  const [channel, setChannel]   = useState<NetworkChannel | null>(null);
  const [posts, setPosts]       = useState<ChannelPost[]>([]);
  const [loading, setLoading]   = useState(false);
  const [posting, setPosting]   = useState(false);
  const [draft, setDraft]       = useState('');
  const bottomRef               = useRef<HTMLDivElement>(null);
  const { on, off }                  = useBooxSocket();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ch, postsRes] = await Promise.all([
        networkChannelApi.get(channelId),
        networkChannelApi.posts(channelId, { limit: 30 }),
      ]);
      setChannel(ch);
      setPosts(postsRes.items.reverse());
    } catch {
      toast.error('Failed to load channel');
    } finally {
      setLoading(false);
    }
  }, [channelId]);

  useEffect(() => { load(); }, [load]);

  // Live WS: new posts
  useEffect(() => {
    const handler = (payload: unknown) => {
      const post = payload as ChannelPost;
      if (post.channel_id === channelId) {
        setPosts((prev) => {
          if (prev.some((p) => p.id === post.id)) return prev;
          return [...prev, post];
        });
      }
    };
    on('network.channel.post.new', handler);
    return () => off('network.channel.post.new', handler);
  }, [on, off, channelId]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [posts]);

  async function handlePost() {
    if (!draft.trim()) return;
    setPosting(true);
    const body = draft.trim();
    setDraft('');
    try {
      const post = await networkChannelApi.createPost(channelId, { body });
      setPosts((prev) => {
        if (prev.some((p) => p.id === post.id)) return prev;
        return [...prev, post];
      });
    } catch {
      toast.error('Failed to post');
      setDraft(body);
    } finally {
      setPosting(false);
    }
  }

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-3 border-b border-border px-4 py-3">
        {onBack && (
          <Button variant="ghost" size="icon" onClick={onBack}>
            <ArrowLeft className="size-4" />
          </Button>
        )}
        <Hash className="size-4 text-muted-foreground" />
        <span className="font-semibold">{channel?.name ?? '…'}</span>
        {channel && (
          <Badge variant="secondary" className="text-xs">{channel.member_count} members</Badge>
        )}
      </div>

      <ScrollArea className="flex-1 px-4 py-3">
        {loading ? (
          <div className="flex flex-col gap-3">
            {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-28 rounded-lg" />)}
          </div>
        ) : posts.length === 0 ? (
          <div className="py-16 text-center text-muted-foreground text-sm">
            No posts yet — be the first to post!
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {posts.map((p) => <PostCard key={p.id} post={p} channelId={channelId} />)}
            <div ref={bottomRef} />
          </div>
        )}
      </ScrollArea>

      {channel?.is_subscribed && (
        <div className="flex items-end gap-2 border-t border-border px-4 py-3">
          <Textarea
            className="min-h-[60px] flex-1 resize-none"
            placeholder="Share your thoughts…"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) handlePost();
            }}
            disabled={posting}
          />
          <Button size="icon" onClick={handlePost} disabled={!draft.trim() || posting}>
            <Send className="size-4" />
          </Button>
        </div>
      )}
      {channel && !channel.is_subscribed && (
        <div className="border-t border-border px-4 py-3 text-center text-sm text-muted-foreground">
          Subscribe to this channel to post and join the discussion
        </div>
      )}
    </div>
  );
}

// ─── Create channel dialog ────────────────────────────────────────────────────

function CreateChannelDialog({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (channel: NetworkChannel) => void;
}) {
  const [name, setName]         = useState('');
  const [desc, setDesc]         = useState('');
  const [type, setType]         = useState('group');
  const [vis, setVis]           = useState('public');
  const [loading, setLoading]   = useState(false);

  async function handleCreate() {
    if (!name.trim()) return;
    setLoading(true);
    try {
      const ch = await networkChannelApi.create({
        name: name.trim(),
        description: desc || undefined,
        channel_type: type,
        visibility: vis,
      });
      toast.success(`Channel #${ch.name} created`);
      onCreated(ch);
      onClose();
      setName(''); setDesc(''); setType('group'); setVis('public');
    } catch {
      toast.error('Failed to create channel');
    } finally {
      setLoading(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={() => onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Create Channel</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-4 py-1">
          <div className="flex flex-col gap-1.5">
            <Label>Channel Name</Label>
            <Input placeholder="e.g. Asia Trade Discussion" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label>Description <span className="text-muted-foreground">(optional)</span></Label>
            <Textarea rows={2} placeholder="What is this channel about…" value={desc} onChange={(e) => setDesc(e.target.value)} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1.5">
              <Label>Type</Label>
              <Select value={type} onValueChange={setType}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(CHANNEL_TYPE_LABEL).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label>Visibility</Label>
              <Select value={vis} onValueChange={setVis}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="public">Public</SelectItem>
                  <SelectItem value="verified_only">Verified Only</SelectItem>
                  <SelectItem value="invite">Invite Only</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={handleCreate} disabled={!name.trim() || loading}>
            {loading ? 'Creating…' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function NetworkChannelsPage() {
  const { channelId } = useParams<{ channelId?: string }>();
  const navigate       = useNavigate();

  const [channels, setChannels]     = useState<NetworkChannel[]>([]);
  const [loading, setLoading]       = useState(false);
  const [subLoading, setSubLoading] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await networkChannelApi.list({ limit: 50 });
      setChannels(res.items);
    } catch {
      toast.error('Failed to load channels');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function handleToggleSub(id: string, isSubscribed: boolean) {
    setSubLoading(id);
    try {
      if (isSubscribed) {
        await networkChannelApi.unsubscribe(id);
        toast.success('Unsubscribed');
      } else {
        await networkChannelApi.subscribe(id);
        toast.success('Subscribed');
      }
      setChannels((prev) =>
        prev.map((c) => c.id === id ? { ...c, is_subscribed: !isSubscribed } : c),
      );
    } catch {
      toast.error('Operation failed');
    } finally {
      setSubLoading(null);
    }
  }

  // Desert two-panel layout on desktop
  const isMobile = window.innerWidth < 768;

  const channelList = (
    <>
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <div className="flex items-center gap-2">
          <Hash className="size-4 text-muted-foreground" />
          <span className="font-semibold">Topic Channels</span>
        </div>
        <div className="flex gap-1">
          <Button variant="ghost" size="icon" onClick={load}>
            <RefreshCw className={cn('size-4', loading && 'animate-spin')} />
          </Button>
          <Button size="sm" onClick={() => setShowCreate(true)}>
            <Plus className="mr-1.5 size-3.5" />Create
          </Button>
        </div>
      </div>
      <ScrollArea className="flex-1 p-4">
        {loading && channels.length === 0 ? (
          <div className="flex flex-col gap-3">
            {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-28 rounded-lg" />)}
          </div>
        ) : channels.length === 0 ? (
          <div className="flex flex-col items-center gap-3 py-16 text-center text-muted-foreground">
            <Hash className="size-10 opacity-30" />
            <p>No channels yet</p>
            <Button onClick={() => setShowCreate(true)}>Create the first channel</Button>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {channels.map((ch) => (
              <ChannelCard
                key={ch.id}
                channel={ch}
                onClick={() => navigate(`/network/channels/${ch.id}`)}
                onToggleSub={handleToggleSub}
                subLoading={subLoading}
              />
            ))}
          </div>
        )}
      </ScrollArea>
    </>
  );

  if (isMobile) {
    if (channelId) {
      return (
        <div className="h-full">
          <ChannelDetailView channelId={channelId} onBack={() => navigate('/network/channels')} />
        </div>
      );
    }
    return (
      <div className="flex h-full flex-col">
        {channelList}
        <CreateChannelDialog
          open={showCreate}
          onClose={() => setShowCreate(false)}
          onCreated={(ch) => {
            setChannels((prev) => [ch, ...prev]);
            navigate(`/network/channels/${ch.id}`);
          }}
        />
      </div>
    );
  }

  // Desktop two-panel
  return (
    <div className="grid h-full" style={{ gridTemplateColumns: '320px 1fr' }}>
      <div className="flex h-full flex-col border-r border-border">
        {channelList}
      </div>
      <div className="h-full overflow-hidden">
        {channelId ? (
          <ChannelDetailView channelId={channelId} />
        ) : (
          <div className="flex h-full flex-col items-center justify-center gap-3 text-muted-foreground">
            <Hash className="size-12 opacity-20" />
            <p className="text-sm">Select a channel</p>
          </div>
        )}
      </div>

      <CreateChannelDialog
        open={showCreate}
        onClose={() => setShowCreate(false)}
        onCreated={(ch) => {
          setChannels((prev) => [ch, ...prev]);
          navigate(`/network/channels/${ch.id}`);
        }}
      />
    </div>
  );
}
